import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { verifyEmail } from "@/lib/api";
import { usePageTitle } from "@/hooks/usePageTitle";
import logo from "@/assets/famibelleza_logo.png";

type Status = "verifying" | "success" | "error";

const STATUS_TITLE_KEYS: Record<Status, string> = {
  verifying: "verifyEmail.verifying",
  success: "verifyEmail.successTitle",
  error: "verifyEmail.failedTitle",
};

export default function VerifyEmail() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [status, setStatus] = useState<Status>(token ? "verifying" : "error");
  usePageTitle(t(STATUS_TITLE_KEYS[status]));
  const [error, setError] = useState<string | null>(null);
  const ranOnce = useRef(false);

  useEffect(() => {
    if (!token || ranOnce.current) return;
    ranOnce.current = true;

    verifyEmail(token)
      .then(() => setStatus("success"))
      .catch(err => {
        setError(err instanceof Error ? err.message : null);
        setStatus("error");
      });
  }, [token]);

  return (
    <div className="relative flex items-center justify-center py-12 px-4 overflow-hidden">
      <img src={logo} alt="" className="absolute top-0 w-1/2 h-full object-contain z-0 opacity-10 blur-md" />
      <div className="relative z-10 max-w-md w-full bg-white rounded-2xl shadow-md p-8 text-center space-y-4">
        {status === "verifying" && (
          <>
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-brand/10 animate-pulse">
              <svg className="h-6 w-6 text-brand" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-brand">{t("verifyEmail.verifying")}</h1>
          </>
        )}

        {status === "success" && (
          <>
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
              <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900">{t("verifyEmail.successTitle")}</h1>
            <p className="text-gray-600">{t("verifyEmail.successDescription")}</p>
            <Link
              to="/login"
              className="inline-block py-3 px-6 bg-brand text-white font-semibold rounded-2xl hover:bg-brand/90 transition-all duration-200 shadow-sm"
            >
              {t("verifyEmail.logIn")}
            </Link>
          </>
        )}

        {status === "error" && (
          <>
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
              <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900">{t("verifyEmail.failedTitle")}</h1>
            <p className="text-gray-600">{error ?? t("verifyEmail.missingToken")}</p>
            <Link to="/login" className="text-sm font-medium text-brand hover:text-brand/80">{t("verifyEmail.backToLogin")}</Link>
          </>
        )}
      </div>
    </div>
  );
}
